import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  developmentService,
  type DevelopmentCurrentTrader,
  type DevelopmentTraderDirectoryItem,
} from '@/services/development/development.service';

const STATUS_LABELS = {
  ACTIVE: 'Активен',
  INACTIVE: 'Неактивен',
  ARCHIVED: 'Архив',
  NOT_FOUND: 'Не найден',
};
const STATUS_COLORS = {
  ACTIVE: 'success',
  INACTIVE: 'default',
  ARCHIVED: 'warning',
  NOT_FOUND: 'error',
} as const;
const numberFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

const formatNumber = (value: string | null) => value === null ? '—' : numberFormatter.format(Number(value));
const formatSheetTeamLead = (value: string | null) => value?.replace(/\n/g, ' · ') || '—';

function ComparisonValue({ oldValue, currentValue }: { oldValue: string | null; currentValue: string | null }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" color="text.secondary">Лист: {formatNumber(oldValue)}</Typography>
      <Typography variant="body2" fontWeight={700}>БД: {formatNumber(currentValue)}</Typography>
    </Stack>
  );
}

function ChatLinks({ chats }: { chats: DevelopmentCurrentTrader['trader_chats'] }) {
  if (!chats.length) return <>—</>;

  return (
    <Stack spacing={0.25}>
      {chats.map((chat) => chat.invite_url ? (
        <Link key={`${chat.title}-${chat.invite_url}`} href={chat.invite_url} target="_blank" rel="noreferrer" variant="body2">
          {chat.title}
        </Link>
      ) : (
        <Typography key={chat.title} variant="body2">{chat.title}</Typography>
      ))}
    </Stack>
  );
}

function ChatComparison({
  baselineUrl,
  baselineAdded,
  chats,
}: {
  baselineUrl: string | null;
  baselineAdded: boolean | null;
  chats: DevelopmentCurrentTrader['trader_chats'];
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        Лист:{' '}
        {baselineUrl ? <Link href={baselineUrl} target="_blank" rel="noreferrer">ссылка</Link> : baselineAdded ? 'добавлен' : 'нет'}
      </Typography>
      <Box><Typography variant="caption" color="text.secondary">БД: </Typography><ChatLinks chats={chats} /></Box>
    </Stack>
  );
}

export default function TraderDirectory() {
  const [items, setItems] = useState<DevelopmentTraderDirectoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive' | 'archived' | 'not_found'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError('');

      developmentService
        .getTraderDirectory({ search: search.trim() || undefined, status, page })
        .then((response) => {
          setItems(response.items);
          setTotal(response.total);
          setPages(response.pages);
        })
        .catch(() => setError('Не удалось загрузить сравнительную таблицу трейдеров'))
        .finally(() => setLoading(false));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [search, status, page]);

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={0.5}>Сверка трейдеров</Typography>
      <Typography color="text.secondary" mb={3}>
        «Лист» — исторические значения из Traders/rates; «БД» — актуальные данные админки.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Поиск"
            placeholder="Трейдер, логин или тимлид"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            fullWidth
          />
          <FormControl sx={{ minWidth: 190 }}>
            <InputLabel>Статус в БД</InputLabel>
            <Select
              label="Статус в БД"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as typeof status);
                setPage(1);
              }}
            >
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="active">Активные</MenuItem>
              <MenuItem value="inactive">Неактивные</MenuItem>
              <MenuItem value="archived">Архив</MenuItem>
              <MenuItem value="not_found">Нет в админке</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Тимлид / группа</TableCell>
                <TableCell>Трейдер</TableCell>
                <TableCell>Чат тимлида</TableCell>
                <TableCell>Чат трейдера</TableCell>
                <TableCell>Ставка IN</TableCell>
                <TableCell>Ставка OUT</TableCell>
                <TableCell>Рефка</TableCell>
                <TableCell>Circle</TableCell>
                <TableCell>Safe Deposit</TableCell>
                <TableCell>Статус в БД</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}><CircularProgress size={28} /></TableCell>
                </TableRow>
              ) : items.length ? (
                items.map((trader) => (
                  <TableRow key={trader.id || trader.name} hover>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">Лист: {formatSheetTeamLead(trader.baseline?.team_lead || null)}</Typography>
                      <Typography fontWeight={700}>БД: {trader.current.team_lead || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">{trader.current.group_name || 'Без группы'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={700}>{trader.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {trader.username || '—'}
                        {trader.telegram_nickname ? ` · @${trader.telegram_nickname.replace(/^@/, '')}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ChatComparison
                        baselineUrl={trader.baseline?.team_lead_chat_url || null}
                        baselineAdded={Boolean(trader.baseline?.team_lead_chat_url)}
                        chats={trader.current.team_lead_chats}
                      />
                    </TableCell>
                    <TableCell>
                      <ChatComparison
                        baselineUrl={trader.baseline?.trader_chat_url || null}
                        baselineAdded={trader.baseline?.chat_added_in_admin || null}
                        chats={trader.current.trader_chats}
                      />
                    </TableCell>
                    <TableCell><ComparisonValue oldValue={trader.baseline?.rate_in || null} currentValue={trader.current.rate_in} /></TableCell>
                    <TableCell><ComparisonValue oldValue={trader.baseline?.rate_out || null} currentValue={trader.current.rate_out} /></TableCell>
                    <TableCell><ComparisonValue oldValue={trader.baseline?.referral_percent || null} currentValue={trader.current.referral_percent} /></TableCell>
                    <TableCell><ComparisonValue oldValue={trader.baseline?.circle || null} currentValue={trader.current.circle} /></TableCell>
                    <TableCell><ComparisonValue oldValue={trader.baseline?.safe_deposit || null} currentValue={trader.current.safe_deposit} /></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[trader.current.status]}
                        color={STATUS_COLORS[trader.current.status]}
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Лист: {trader.baseline ? (trader.baseline.actual_in_admin ? 'да' : 'нет') : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>Трейдеры не найдены</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography color="text.secondary">Всего: {total}</Typography>
        {pages > 1 && <Pagination count={pages} page={page} onChange={(_, nextPage) => setPage(nextPage)} color="primary" />}
      </Stack>
    </Box>
  );
}
